import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHammer, 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faInfoCircle,
  faTable,
  faUserCheck,
  faComment,
  faEye,
  faFilePdf,
  faPencil
} from '@fortawesome/free-solid-svg-icons';
import { getCustomers } from '../../../apis/customers';
import { getReviewers } from '../../../apis/reviewers';
import { createSteelTest, getSteelTest } from '../../../apis/steelTesting';
import { formatDate, ddmmyyyyToYYYYMMDD } from '../../../utils/dateFormatter';

const SteelTesting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPreview, setShowPreview] = useState(false);
  const [savedSteelTestId, setSavedSteelTestId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
  const [showEnvSuggestion, setShowEnvSuggestion] = useState(false);
  const [showULRSuggestion, setShowULRSuggestion] = useState(false);

  // Reviewer selection state
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [reviewerInfo, setReviewerInfo] = useState({
    name: 'Lalita S. Dussa',
    designation: 'Quality Manager',
    graduation: 'B.Tech.(Civil)'
  });

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomersData = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError(null);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomersError('Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomersData();
  }, []);

  // Load existing steel test when opening with ?id= (e.g. from dashboard View)
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    const loadTest = async () => {
      try {
        const test = await getSteelTest(Number(id));
        if (!test) return;
        const toInput = (d) => (d ? ddmmyyyyToYYYYMMDD(d) : '');
        setFormData(prev => ({
          ...prev,
          customer_id: test.customer_id != null ? String(test.customer_id) : '',
          quantity: test.quantity != null ? String(test.quantity) : '1',
          sample_description: test.sample_description || '',
          date_of_receipt: toInput(test.date_of_receipt) || prev.date_of_receipt,
          reference_number: test.reference_number || '',
          condition_of_specimen: test.condition_of_specimen || 'Acceptable',
          make: test.make || '',
          machine_used_for_testing: test.machine_used_for_testing || '',
          location_test: test.location_test || '',
          capacity_range: test.capacity_range || '',
          test_method: test.test_method || '',
          diameter_of_steel: test.diameter_of_steel != null ? String(test.diameter_of_steel) : '',
          date_of_report: toInput(test.date_of_report) || prev.date_of_report,
          url_number: test.url_number || '',
          job_code_number: test.job_code_number || '',
          date_of_testing: toInput(test.date_of_testing) || prev.date_of_testing,
          grade_of_specimen: test.grade_of_specimen || '',
          calibration_due_date: toInput(test.calibration_due_date) || prev.calibration_due_date,
          environmental_condition: test.environmental_condition || '',
          tested_by_name: test.tested_by_name || '',
          tested_by_date: toInput(test.tested_by_date) || prev.tested_by_date,
          checked_by_name: test.checked_by_name || '',
          checked_by_date: toInput(test.checked_by_date) || prev.checked_by_date,
          verified_by_name: test.verified_by_name || '',
          verified_by_date: toInput(test.verified_by_date) || prev.verified_by_date,
          reviewed_by: test.reviewed_by || '',
          authorized_by: test.authorized_by || '',
          remarks: test.remarks || '',
          ...([1, 2, 3].reduce((acc, i) => ({
            ...acc,
            [`id_mark_${i}`]: test[`id_mark_${i}`] ?? '',
            [`dia_${i}`]: test[`dia_${i}`] != null ? String(test[`dia_${i}`]) : '',
            [`weight_${i}`]: test[`weight_${i}`] != null ? String(test[`weight_${i}`]) : '',
            [`length_${i}`]: test[`length_${i}`] != null ? String(test[`length_${i}`]) : '',
            [`wt_per_m_${i}`]: test[`wt_per_m_${i}`] != null ? String(test[`wt_per_m_${i}`]) : '',
            [`c_area_${i}`]: test[`c_area_${i}`] != null ? String(test[`c_area_${i}`]) : '',
            [`gl_${i}`]: test[`gl_${i}`] != null ? String(test[`gl_${i}`]) : '',
            [`hgl_${i}`]: test[`hgl_${i}`] != null ? String(test[`hgl_${i}`]) : '',
            [`fl_${i}`]: test[`fl_${i}`] != null ? String(test[`fl_${i}`]) : '',
            [`elongation_${i}`]: test[`elongation_${i}`] != null ? String(test[`elongation_${i}`]) : '',
            [`yield_load_${i}`]: test[`yield_load_${i}`] != null ? String(test[`yield_load_${i}`]) : '',
            [`max_load_${i}`]: test[`max_load_${i}`] != null ? String(test[`max_load_${i}`]) : '',
            [`proof_yield_stress_${i}`]: test[`proof_yield_stress_${i}`] != null ? String(test[`proof_yield_stress_${i}`]) : '',
            [`ultimate_tensile_stress_${i}`]: test[`ultimate_tensile_stress_${i}`] != null ? String(test[`ultimate_tensile_stress_${i}`]) : '',
            [`bend_rebend_${i}`]: test[`bend_rebend_${i}`] ?? ''
          }), {}))
        }));
        setSavedSteelTestId(test.id);
        if (test.customer_name) setCustomerSearchTerm(test.customer_name);
      } catch (err) {
        console.error('Error loading steel test:', err);
      }
    };
    loadTest();
  }, [searchParams]);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
        (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Fetch reviewers from database
  useEffect(() => {
    const fetchReviewers = async () => {
      setLoadingReviewers(true);
      try {
        const reviewersData = await getReviewers();
        setReviewers(reviewersData);
        
        // Set default reviewer if available
        if (reviewersData.length > 0) {
          const defaultReviewer = reviewersData[0];
          setReviewerInfo({
            name: defaultReviewer.name,
            designation: defaultReviewer.designation,
            graduation: defaultReviewer.graduation
          });
          
          // Update form data with default reviewer
          setFormData(prev => ({
            ...prev,
            reviewed_by: `${defaultReviewer.name} - ${defaultReviewer.designation}`
          }));
        }
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        // Fallback to hardcoded reviewers if API fails
        const fallbackReviewers = [
          { id: 1, name: 'Lalita S. Dussa', designation: 'Quality Manager', graduation: 'B.Tech.(Civil)' },
          { id: 2, name: 'Harsha Prakarsha Sangave', designation: 'Quality Manager', graduation: 'M.E(Civil-Structures)' },
          { id: 3, name: 'Amol A Adam', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 4, name: 'Aaquib J. Shaikh', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 5, name: 'Prakarsha A. Sangave', designation: 'Chief Executive Officer', graduation: 'M.E(Civil-Structures)' }
        ];
        setReviewers(fallbackReviewers);
        
        // Set default reviewer from fallback
        if (fallbackReviewers.length > 0) {
          const defaultReviewer = fallbackReviewers[0];
          setReviewerInfo({
            name: defaultReviewer.name,
            designation: defaultReviewer.designation,
            graduation: defaultReviewer.graduation
          });
          
          // Update form data with default reviewer
          setFormData(prev => ({
            ...prev,
            reviewed_by: `${defaultReviewer.name} - ${defaultReviewer.designation}`
          }));
        }
      } finally {
        setLoadingReviewers(false);
      }
    };

    fetchReviewers();
  }, []);

  // Add CSS styles for form-select to match Cement Testing design
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .form-select {
        background-color: #1C2333 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
      }
      
      .form-select:focus {
        background-color: #1C2333 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .form-select option {
        background-color: #1C2333 !important;
        color: white !important;
      }
      
      .form-control, .form-select {
        background-color: #1C2333 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
        border-radius: 8px !important;
        padding: 12px 15px !important;
      }
      
      .form-control:focus, .form-select:focus {
        background-color: #1C2333 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
        outline: none !important;
      }
      
      .form-control::placeholder {
        color: #adb5bd !important;
        opacity: 0.8 !important;
      }
      
      .card {
        background-color: #1C2333 !important;
        border: 2px solid #6c757d !important;
        border-radius: 12px !important;
      }
      
      .card-header {
        background-color: #6c757d !important;
        color: white !important;
        border-bottom: 2px solid #6c757d !important;
        border-radius: 10px 10px 0 0 !important;
        padding: 15px 20px !important;
      }
      
      .card-body {
        background-color: #1C2333 !important;
        color: white !important;
        padding: 20px !important;
      }
      
      .form-label {
        color: #e2e8f0 !important;
        font-weight: 600 !important;
        font-size: 0.95rem !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        margin-bottom: 8px !important;
      }
      
      .form-label.required-field::after {
        content: " *";
        color: #FFA500 !important;
      }
      
      .table {
        background-color: #1C2333 !important;
        color: white !important;
      }
      
      .table thead th {
        background-color: #2D3748 !important;
        color: white !important;
        border: 1px solid #4A5568 !important;
        font-weight: 700 !important;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .table tbody td {
        background-color: #1C2333 !important;
        color: white !important;
        border: 1px solid #2D3748 !important;
        vertical-align: middle !important;
      }
      
      .table-highlight-blue {
        background-color: rgba(49, 130, 206, 0.4) !important;
        color: white !important;
      }

      .table tr:hover td {
        background-color: #242B3C !important;
      }
      
      .table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .table tbody td:first-child {
        text-align: center !important;
        font-weight: 700 !important;
        color: white !important;
        background-color: #242B3C !important;
      }
      
      .table .form-control {
        background-color: #242B3C !important;
        color: white !important;
        border: 1px solid #4A5568 !important;
        border-radius: 4px !important;
      }
      
      .table .form-control:focus {
        background-color: #242B3C !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.1rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .table .form-control[readonly] {
        background-color: #495057 !important;
        color: #ffffff !important;
        cursor: not-allowed !important;
      }
      
      textarea.form-control {
        min-height: 100px !important;
        resize: vertical !important;
      }
      
      input[type="date"] {
        color-scheme: dark !important;
      }
      
      input[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(1) !important;
        cursor: pointer !important;
      }
      
      h6 {
        color: #FFA500 !important;
        font-weight: 600 !important;
        margin-bottom: 15px !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
      }

      .table {
        margin-bottom: 0 !important;
        border-collapse: separate !important;
        border-spacing: 0 !important;
      }
      
      .table thead th:first-child {
        border-top-left-radius: 8px !important;
      }
      
      .table thead th:last-child {
        border-top-right-radius: 8px !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    quantity: '1',
    sample_description: '',
    date_of_receipt: new Date().toISOString().split('T')[0],
    reference_number: '',
    condition_of_specimen: 'Acceptable',
    make: '',
    machine_used_for_testing: 'Universal Testing Machine (UTM)',
    location_test: 'Permanent',
    capacity_range: '',
    test_method: 'IS 1786: 2008 (RA 2018)',
    diameter_of_steel: '',
    date_of_report: new Date().toISOString().split('T')[0],
    url_number: '',
    job_code_number: '',
    date_of_testing: new Date().toISOString().split('T')[0],
    grade_of_specimen: '',
    calibration_due_date: '2026-06-02',
    environmental_condition: '',
    
    // Test Results - Multiple rows with 16 columns each
    // Row 1
    sr_no_1: '1',
    id_mark_1: '',
    dia_1: '',
    weight_1: '',
    length_1: '',
    wt_per_m_1: '',
    c_area_1: '',
    gl_1: '',
    hgl_1: '',
    fl_1: '',
    elongation_1: '',
    yield_load_1: '',
    max_load_1: '',
    proof_yield_stress_1: '',
    ultimate_tensile_stress_1: '',
    bend_rebend_1: '',
    // Row 2
    sr_no_2: '2',
    id_mark_2: '',
    dia_2: '',
    weight_2: '',
    length_2: '',
    wt_per_m_2: '',
    c_area_2: '',
    gl_2: '',
    hgl_2: '',
    fl_2: '',
    elongation_2: '',
    yield_load_2: '',
    max_load_2: '',
    proof_yield_stress_2: '',
    ultimate_tensile_stress_2: '',
    bend_rebend_2: '',
    // Row 3
    sr_no_3: '3',
    id_mark_3: '',
    dia_3: '',
    weight_3: '',
    length_3: '',
    wt_per_m_3: '',
    c_area_3: '',
    gl_3: '',
    hgl_3: '',
    fl_3: '',
    elongation_3: '',
    yield_load_3: '',
    max_load_3: '',
    proof_yield_stress_3: '',
    ultimate_tensile_stress_3: '',
    bend_rebend_3: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  const getMinMassValue = (diameter) => {
    const dia = parseInt(diameter);
    const mapping = {
      6: (0.222 * 0.93).toFixed(3),
      8: (0.395 * 0.93).toFixed(3),
      10: (0.617 * 0.93).toFixed(3),
      12: (0.888 * 0.95).toFixed(3),
      16: (1.58 * 0.95).toFixed(3),
      20: (2.47 * 0.97).toFixed(3),
      25: (3.85 * 0.97).toFixed(3),
      28: (4.83 * 0.97).toFixed(3),
      32: (6.31 * 0.97).toFixed(3),
      36: (7.99 * 0.97).toFixed(3),
      40: (9.86 * 0.97).toFixed(3)
    };
    return mapping[dia] || '';
  };

  const getMaxMassValue = (diameter) => {
    const dia = parseInt(diameter);
    const mapping = {
      6: (0.222 * 1.07).toFixed(3),
      8: (0.395 * 1.07).toFixed(3),
      10: (0.617 * 1.07).toFixed(3),
      12: (0.888 * 1.05).toFixed(3),
      16: (1.58 * 1.05).toFixed(3),
      20: (2.47 * 1.03).toFixed(3),
      25: (3.85 * 1.03).toFixed(3),
      28: (4.83 * 1.03).toFixed(3),
      32: (6.31 * 1.03).toFixed(3),
      36: (7.99 * 1.03).toFixed(3),
      40: (9.86 * 1.03).toFixed(3)
    };
    return mapping[dia] || '';
  };

  const getMinElongationValue = (grade) => {
    const mapping = {
      "Fe 415": "14.5",
      "Fe 415D": "18",
      "Fe 500": "12",
      "Fe 500D": "16",
      "Fe 550": "10",
      "Fe 550D": "14.5",
      "Fe 600": "10"
    };
    return mapping[grade] || '';
  };

  const getMinYieldStressValue = (grade) => {
    const mapping = {
      "Fe 415": "415",
      "Fe 415D": "415",
      "Fe 500": "500",
      "Fe 500D": "500",
      "Fe 550": "550",
      "Fe 550D": "550",
      "Fe 600": "600"
    };
    return mapping[grade] || '';
  };

  const getMinTensileStrengthValue = (grade) => {
    const mapping = {
      "Fe 415": "485",
      "Fe 415D": "500",
      "Fe 500": "545",
      "Fe 500D": "565",
      "Fe 550": "585",
      "Fe 550D": "600",
      "Fe 600": "660"
    };
    return mapping[grade] || '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'diameter_of_steel') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        dia_1: value,
        dia_2: value,
        dia_3: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  useEffect(() => {
    const quantity = Number(formData.quantity) || 0;
    const updates = {};

    for (let i = 1; i <= quantity; i += 1) {
      const massPerMeter = parseFloat(formData[`wt_per_m_${i}`]);
      if (!Number.isNaN(massPerMeter) && massPerMeter > 0) {
        updates[`c_area_${i}`] = (massPerMeter / 0.00785).toFixed(2);
      } else {
        updates[`c_area_${i}`] = '';
      }
    }

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, [
    formData.quantity,
    formData.wt_per_m_1,
    formData.wt_per_m_2,
    formData.wt_per_m_3,
  ]);

  const observationBarCount = Math.max(1, Number(formData.quantity) || 0);
  const observationBarKeys = ['bar1', 'bar2', 'bar3'].slice(0, observationBarCount);

  const bendRebendSelect = (barIndex) => (
    <Form.Select
      size="sm"
      name={`bend_rebend_${barIndex}`}
      value={formData[`bend_rebend_${barIndex}`] || ''}
      onChange={handleInputChange}
      style={{ fontSize: '0.85rem', textAlign: 'center' }}
    >
      <option value="">Select</option>
      <option value="No Crack">No Crack</option>
      <option value="Crack Observed">Crack Observed</option>
    </Form.Select>
  );

  const TEST_STATUS_ROWS = [
    { srNo: 1, label: 'Mass Per Meter (Kg/m)', key: 'mass' },
    { srNo: 2, label: '% of Elongation', key: 'elongation' },
    { srNo: 3, label: 'Yield Stress (N/mm²)', key: 'yield' },
    { srNo: 4, label: 'Tensile Strength (N/mm²)', key: 'tensile' },
    { srNo: 6, label: 'Bend Test (180°)', key: 'bend' },
    { srNo: 7, label: 'Re-Bend Test (123° / 157.5°)', key: 'rebend' },
  ];

  const evaluateNumericStatus = (value, minStr, maxStr) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) return 'UNSAFE';

    const min = minStr && minStr !== '----' ? parseFloat(minStr) : null;
    const max = maxStr && maxStr !== '----' ? parseFloat(maxStr) : null;

    const hasMin = min != null && !Number.isNaN(min);
    const hasMax = max != null && !Number.isNaN(max);

    if (!hasMin && !hasMax) return 'UNSAFE';

    const okMin = hasMin ? numericValue >= min : true;
    const okMax = hasMax ? numericValue <= max : true;

    return okMin && okMax ? 'OK' : 'UNSAFE';
  };

  const getBarTestStatus = (rowKey, barIndex) => {
    switch (rowKey) {
      case 'mass':
        return evaluateNumericStatus(
          formData[`wt_per_m_${barIndex}`],
          getMinMassValue(formData.diameter_of_steel),
          getMaxMassValue(formData.diameter_of_steel),
        );
      case 'elongation':
        return evaluateNumericStatus(
          formData[`elongation_${barIndex}`],
          getMinElongationValue(formData.grade_of_specimen),
          null,
        );
      case 'yield':
        return evaluateNumericStatus(
          formData[`proof_yield_stress_${barIndex}`],
          getMinYieldStressValue(formData.grade_of_specimen),
          null,
        );
      case 'tensile':
        return evaluateNumericStatus(
          formData[`ultimate_tensile_stress_${barIndex}`],
          getMinTensileStrengthValue(formData.grade_of_specimen),
          null,
        );
      case 'bend': {
        const value = (formData[`bend_rebend_${barIndex}`] || '').toString();
        return value === 'No Crack' ? 'OK' : 'UNSAFE';
      }
      case 'rebend': {
        const value = (formData[`bend_rebend_${barIndex}`] || '').toString();
        return value === 'No Crack' ? 'OK' : 'UNSAFE';
      }
      default:
        return 'UNSAFE';
    }
  };

  const renderTestStatusTable = (readOnly = false) => (
    <table
      className="table transparent-table"
      style={{
        fontSize: '1rem',
        width: '100%',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
      }}
    >
      <thead>
        <tr>
          <th
            rowSpan={2}
            style={{
              width: '8%',
              textAlign: 'center',
              verticalAlign: 'middle',
              fontWeight: '700',
              padding: '12px 8px',
              border: '1px solid #dee2e6',
            }}
          >
            Sr. No.
          </th>
          <th
            rowSpan={2}
            style={{
              width: '32%',
              textAlign: 'center',
              verticalAlign: 'middle',
              fontWeight: '700',
              padding: '12px 8px',
              border: '1px solid #dee2e6',
            }}
          >
            Test Parameter
          </th>
          <th
            colSpan={observationBarCount}
            style={{
              textAlign: 'center',
              verticalAlign: 'middle',
              fontWeight: '700',
              padding: '12px 8px',
              border: '1px solid #dee2e6',
            }}
          >
            Test Result
          </th>
        </tr>
        <tr>
          {['Bar-1', 'Bar-2', 'Bar-3'].slice(0, observationBarCount).map((barLabel) => (
            <th
              key={barLabel}
              style={{
                textAlign: 'center',
                padding: '10px 8px',
                border: '1px solid #dee2e6',
              }}
            >
              {barLabel}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {TEST_STATUS_ROWS.map((row) => (
          <tr key={row.key}>
            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{row.srNo}</td>
            <td>{row.label}</td>
            {observationBarKeys.map((bar, index) => {
              const status = getBarTestStatus(row.key, index + 1);
              return (
                <td key={`${row.key}-${bar}`} style={{ textAlign: 'center' }}>
                  {readOnly ? (
                    <span
                      style={{
                        fontWeight: 600,
                        color: status === 'OK' ? '#28a745' : '#dc3545',
                      }}
                    >
                      {status}
                    </span>
                  ) : (
                    <Form.Control
                      size="sm"
                      type="text"
                      value={status}
                      readOnly
                      style={{
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                        fontWeight: 600,
                        color: status === 'OK' ? '#28a745' : '#dc3545',
                      }}
                    />
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id.toString(),
      customer_site_address: customer.address || customer.site_address || ''
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm('');
  };

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm('');
  };

  // Handle reviewer selection change
  const handleReviewerChange = (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    if (selectedOption.value) {
      const reviewerData = {
        name: selectedOption.getAttribute('data-name'),
        designation: selectedOption.getAttribute('data-designation'),
        graduation: selectedOption.getAttribute('data-graduation')
      };
      
      setReviewerInfo(reviewerData);
      
      // Update form data with reviewer information
      setFormData(prev => ({
        ...prev,
        reviewed_by: `${reviewerData.name} - ${reviewerData.designation}`
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quantity || !['1', '2', '3'].includes(formData.quantity)) {
      alert('Please select Quantity (1, 2, or 3).');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        customer_id: formData.customer_id || null,
        quantity: Number(formData.quantity) || 1,
        sample_description: formData.sample_description || null,
        date_of_receipt: formData.date_of_receipt || null,
        reference_number: formData.reference_number || null,
        condition_of_specimen: formData.condition_of_specimen || null,
        make: formData.make || null,
        machine_used_for_testing: formData.machine_used_for_testing || null,
        location_test: formData.location_test || null,
        capacity_range: formData.capacity_range || null,
        test_method: formData.test_method || null,
        diameter_of_steel: formData.diameter_of_steel ? Number(formData.diameter_of_steel) : null,
        date_of_report: formData.date_of_report || null,
        url_number: formData.url_number || null,
        job_code_number: formData.job_code_number || null,
        date_of_testing: formData.date_of_testing || null,
        grade_of_specimen: formData.grade_of_specimen || null,
        calibration_due_date: formData.calibration_due_date || null,
        environmental_condition: formData.environmental_condition || null,
        tested_by_name: formData.tested_by_name || null,
        tested_by_date: formData.tested_by_date || null,
        checked_by_name: formData.checked_by_name || null,
        checked_by_date: formData.checked_by_date || null,
        verified_by_name: formData.verified_by_name || null,
        verified_by_date: formData.verified_by_date || null,
        reviewed_by: formData.reviewed_by || null,
        authorized_by: formData.authorized_by || null,
        remarks: formData.remarks || null,
      };
      const qty = Number(formData.quantity) || 1;
      for (let i = 1; i <= qty; i++) {
        payload[`id_mark_${i}`] = formData[`id_mark_${i}`] || null;
        payload[`dia_${i}`] = formData[`dia_${i}`] || null;
        payload[`wt_per_m_${i}`] = formData[`wt_per_m_${i}`] || null;
        payload[`c_area_${i}`] = formData[`c_area_${i}`] || null;
        payload[`elongation_${i}`] = formData[`elongation_${i}`] || null;
        payload[`proof_yield_stress_${i}`] = formData[`proof_yield_stress_${i}`] || null;
        payload[`ultimate_tensile_stress_${i}`] = formData[`ultimate_tensile_stress_${i}`] || null;
        payload[`bend_rebend_${i}`] = formData[`bend_rebend_${i}`] || null;
      }
      // Min/Max specified limits (same as form display) - stored in DB and shown in report
      payload.spec_min_1 = getMinMassValue(formData.diameter_of_steel) || null;
      payload.spec_max_1 = getMaxMassValue(formData.diameter_of_steel) || null;
      payload.spec_min_2 = null;
      payload.spec_max_2 = null;
      payload.spec_min_3 = getMinElongationValue(formData.grade_of_specimen) || null;
      payload.spec_max_3 = null;
      payload.spec_min_4 = getMinYieldStressValue(formData.grade_of_specimen) || null;
      payload.spec_max_4 = null;
      payload.spec_min_5 = getMinTensileStrengthValue(formData.grade_of_specimen) || null;
      payload.spec_max_5 = null;
      payload.spec_min_6 = 'No Crack';
      payload.spec_max_6 = null;
      payload.spec_min_7 = 'No Crack';
      payload.spec_max_7 = null;
      const res = await createSteelTest(payload);
      if (res && res.id != null) setSavedSteelTestId(res.id);
      setShowPreview(true);
    } catch (err) {
      console.error('Steel test save failed:', err.response?.status, err.response?.data || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewObservationSheet = () => {
    if (savedSteelTestId != null) {
      window.open(`/SteelTesting/SteelTestObservationSheet.html?id=${savedSteelTestId}`, '_blank');
      return;
    }
    const params = new URLSearchParams();
    params.append('sample_test_code', formData.url_number || formData.job_code_number || '');
    params.append('sample_description', formData.sample_description || '');
    params.append('date_of_receipt', formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '');
    params.append('date_of_testing', formData.date_of_testing ? formatDate(formData.date_of_testing) : '');
    params.append('date_of_report', formData.date_of_report ? formatDate(formData.date_of_report) : '');
    params.append('quantity', formData.quantity || '1');
    params.append('grade_of_specimen', formData.grade_of_specimen || '');
    params.append('diameter_of_steel', formData.diameter_of_steel || '');
    params.append('condition_of_specimen', formData.condition_of_specimen || '');
    params.append('make', formData.make || '');
    params.append('machine_used_for_testing', formData.machine_used_for_testing || '');
    params.append('test_method', formData.test_method || '');
    params.append('location_test', formData.location_test || '');
    params.append('capacity_range', formData.capacity_range || '');
    params.append('environmental_condition', formData.environmental_condition || '');
    params.append('calibration_due_date', formData.calibration_due_date ? formatDate(formData.calibration_due_date) : '');
    for (let i = 1; i <= 3; i++) {
      params.append(`id_mark_${i}`, formData[`id_mark_${i}`] || '');
      params.append(`dia_${i}`, formData[`dia_${i}`] || '');
      params.append(`weight_${i}`, formData[`weight_${i}`] || '');
      params.append(`length_${i}`, formData[`length_${i}`] || '');
      params.append(`wt_per_m_${i}`, formData[`wt_per_m_${i}`] || '');
      params.append(`c_area_${i}`, formData[`c_area_${i}`] || '');
      params.append(`gl_${i}`, formData[`gl_${i}`] || '');
      params.append(`hgl_${i}`, formData[`hgl_${i}`] || '');
      params.append(`fl_${i}`, formData[`fl_${i}`] || '');
      params.append(`elongation_${i}`, formData[`elongation_${i}`] || '');
      params.append(`yield_load_${i}`, formData[`yield_load_${i}`] || '');
      params.append(`max_load_${i}`, formData[`max_load_${i}`] || '');
      params.append(`proof_yield_stress_${i}`, formData[`proof_yield_stress_${i}`] || '');
      params.append(`ultimate_tensile_stress_${i}`, formData[`ultimate_tensile_stress_${i}`] || '');
      params.append(`bend_rebend_${i}`, formData[`bend_rebend_${i}`] || '');
    }
    params.append('remarks', formData.remarks || '');
    params.append('tested_by_name', formData.tested_by_name || '');
    params.append('tested_by_date', formData.tested_by_date ? formatDate(formData.tested_by_date) : '');
    params.append('checked_by_name', formData.checked_by_name || '');
    params.append('checked_by_date', formData.checked_by_date ? formatDate(formData.checked_by_date) : '');
    params.append('verified_by_name', formData.verified_by_name || '');
    params.append('verified_by_date', formData.verified_by_date ? formatDate(formData.verified_by_date) : '');
    window.open(`/SteelTesting/SteelTestObservationSheet.html?${params.toString()}`, '_blank');
  };

  const handleViewReport = () => {
    if (savedSteelTestId != null) {
      window.open(`/SteelTesting/SteelTestReport.html?id=${savedSteelTestId}`, '_blank');
      return;
    }
    const params = new URLSearchParams();
    params.append('customer_name', customerSearchTerm || '');
    params.append('sample_description', formData.sample_description || '');
    params.append('date_of_receipt', formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '');
    params.append('date_of_testing', formData.date_of_testing ? formatDate(formData.date_of_testing) : '');
    params.append('date_of_report', formData.date_of_report ? formatDate(formData.date_of_report) : '');
    params.append('reference_number', formData.reference_number || '');
    params.append('job_code_number', formData.job_code_number || '');
    params.append('url_number', formData.url_number || '');
    params.append('ulr_number', formData.url_number || '');
    params.append('quantity', formData.quantity || '1');
    params.append('grade_of_specimen', formData.grade_of_specimen || '');
    params.append('diameter_of_steel', formData.diameter_of_steel || '');
    params.append('condition_of_specimen', formData.condition_of_specimen || '');
    params.append('make', formData.make || '');
    params.append('machine_used_for_testing', formData.machine_used_for_testing || '');
    params.append('test_method', formData.test_method || '');
    params.append('location_test', formData.location_test || '');
    params.append('capacity_range', formData.capacity_range || '');
    params.append('environmental_condition', formData.environmental_condition || '');
    params.append('calibration_due_date', formData.calibration_due_date ? formatDate(formData.calibration_due_date) : '');
    for (let i = 1; i <= 3; i++) {
      params.append(`wt_per_m_${i}`, formData[`wt_per_m_${i}`] || '');
      params.append(`c_area_${i}`, formData[`c_area_${i}`] || '');
      params.append(`elongation_${i}`, formData[`elongation_${i}`] || '');
      params.append(`proof_yield_stress_${i}`, formData[`proof_yield_stress_${i}`] || '');
      params.append(`ultimate_tensile_stress_${i}`, formData[`ultimate_tensile_stress_${i}`] || '');
      params.append(`bend_rebend_${i}`, formData[`bend_rebend_${i}`] || '');
    }
    params.append('reviewed_by', formData.reviewed_by || '');
    params.append('authorized_by', formData.authorized_by || '');
    window.open(`/SteelTesting/SteelTestReport.html?${params.toString()}`, '_blank');
  };

  const handleEditForm = () => {
    setShowPreview(false);
  };

  const PreviewForm = () => (
    <div style={{ backgroundColor: '#1C2333', minHeight: '100vh', padding: '10px 0' }}>
      <Container fluid="lg">
        <Card style={{
          backgroundColor: '#1C2333',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '15px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          marginBottom: '20px'
        }}>
          <Card.Header style={{
            backgroundColor: '#1C2333',
            borderBottom: '2px solid #FFA500',
            padding: '20px'
          }}>
            <Row className="align-items-center">
              <Col xs={12} md={2} className="text-center mb-2 mb-md-0">
                <img src="/logo.png" alt="Vitrag Associates Logo" height="40" style={{ borderRadius: '5px' }} />
              </Col>
              <Col xs={12} md={8} className="text-center">
                <h2 style={{ color: '#ffffff', fontWeight: '700', margin: '0', fontSize: '1.5rem' }}>
                  <FontAwesomeIcon icon={faHammer} className="me-2" style={{ color: '#FFA500' }} />
                  STEEL TESTING - PREVIEW
                </h2>
              </Col>
              <Col xs={12} md={2} className="text-center mt-2 mt-md-0">
                <img src="/nabl_logo_final.png" alt="NABL Logo" height="40" style={{ borderRadius: '5px' }} />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col className="text-end">
                <Button
                  onClick={handleEditForm}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '10px 20px',
                    fontWeight: '500',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <FontAwesomeIcon icon={faPencil} className="me-2" />
                  Edit
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body style={{ backgroundColor: '#1C2333', padding: '15px' }}>
            {/* Preview data summary */}
            <div className="mb-4">
              <div className="table-responsive">
                <Table style={{ backgroundColor: '#1C2333', color: '#ffffff', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <tbody>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)', width: '22%' }}>Customer Name</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{customerSearchTerm || formData.customer_id || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)', width: '18%' }}>Quantity</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.quantity || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Date of Receipt</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Date of Testing</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.date_of_testing ? formatDate(formData.date_of_testing) : '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Reference Number</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.reference_number || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Job Code Number</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.job_code_number || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Sample Description</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }} colSpan={3}>{formData.sample_description || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Diameter of Steel (mm)</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.diameter_of_steel || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Grade of Specimen</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.grade_of_specimen || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Condition of Specimen</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.condition_of_specimen || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Test Method</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.test_method || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Machine Used</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.machine_used_for_testing || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Location Test</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.location_test || '—'}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Test Results table */}
            <div className="mb-4">
              <h6 style={{ color: '#FFD700', marginBottom: '10px' }}>Test Results</h6>
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <Table style={{ backgroundColor: '#1C2333', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.8rem' }} size="sm">
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Sr. no</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Id mark</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Dia (mm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Weight (gm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Length (mm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Wt/m (kg/m)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>C/Area (mm²)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>GL (mm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>HGL (mm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>FL (mm)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>% Elongation</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Yield Load (kN)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Max Load (kN)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Yield stress (N/mm²)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>UTS (N/mm²)</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Bend/Rebend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((rowNum) => (
                      <tr key={rowNum}>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`sr_no_${rowNum}`] || rowNum}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`id_mark_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`dia_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`weight_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`length_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`wt_per_m_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`c_area_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`gl_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`hgl_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`fl_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`elongation_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`yield_load_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`max_load_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`proof_yield_stress_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`ultimate_tensile_stress_${rowNum}`] || '—'}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`bend_rebend_${rowNum}`] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Observation of Sample & Test Result table */}
            <div className="mb-4">
              <h6 style={{ color: '#FFD700', marginBottom: '10px' }}>Observation of Sample & Test Result</h6>
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <Table style={{ backgroundColor: '#1C2333', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.8rem' }} size="sm">
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center', width: '8%' }}>Sr. No.</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'left', width: '22%' }}>Test Parameter</th>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <th key={b} style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Bar-{b}</th>
                      ))}
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Min</th>
                      <th style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>1</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Mass Per Meter (Kg/m)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`wt_per_m_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{getMinMassValue(formData.diameter_of_steel) || '—'}</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{getMaxMassValue(formData.diameter_of_steel) || '—'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>2</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Cross Sectional Area (mm²)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`c_area_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>3</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>% of Elongation</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`elongation_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{getMinElongationValue(formData.grade_of_specimen) || '—'}</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>4</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Yield Stress (N/mm²)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`proof_yield_stress_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{getMinYieldStressValue(formData.grade_of_specimen) || '—'}</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>5</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Tensile Strength (N/mm²)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`ultimate_tensile_stress_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{getMinTensileStrengthValue(formData.grade_of_specimen) || '—'}</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>6</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Bend Test (180°)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`bend_rebend_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>7</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>Re-Bend Test (123° / 157.5°)</td>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <td key={b} style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>{formData[`bend_rebend_${b}`] || '—'}</td>
                      ))}
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                      <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>----</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-4">
              <h6 style={{ color: '#FFD700', marginBottom: '10px' }}>Test Status</h6>
              <div className="table-responsive">
                <Table style={{ backgroundColor: '#1C2333', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }} size="sm">
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Sr. No.</th>
                      <th rowSpan={2} style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'left' }}>Test Parameter</th>
                      <th colSpan={Math.max(1, Number(formData.quantity || 1))} style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Test Result</th>
                    </tr>
                    <tr>
                      {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => (
                        <th key={b} style={{ padding: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#FFD700', textAlign: 'center' }}>Bar-{b}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TEST_STATUS_ROWS.map((row) => (
                      <tr key={row.key}>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center', fontWeight: 'bold' }}>{row.srNo}</td>
                        <td style={{ padding: '6px', border: '1px solid rgba(255,255,255,0.2)' }}>{row.label}</td>
                        {[1, 2, 3].slice(0, Number(formData.quantity || 1)).map((b) => {
                          const status = getBarTestStatus(row.key, b);
                          return (
                            <td
                              key={`${row.key}-${b}`}
                              style={{
                                padding: '6px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                textAlign: 'center',
                                fontWeight: 600,
                                color: status === 'OK' ? '#90ee90' : '#ff6b6b',
                              }}
                            >
                              {status}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-4">
              <div className="table-responsive">
                <Table style={{ backgroundColor: '#1C2333', color: '#ffffff', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <tbody>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Tested By</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.tested_by_name || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Checked By</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.checked_by_name || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Reviewed By</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.reviewed_by || '—'}</td>
                      <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Authorized By</th>
                      <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>{formData.authorized_by || '—'}</td>
                    </tr>
                    {formData.remarks && (
                      <tr>
                        <th style={{ backgroundColor: '#1C2333', color: '#FFD700', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>Remarks</th>
                        <td style={{ backgroundColor: '#1C2333', color: '#fff', padding: '12px', border: '1px solid rgba(255,255,255,0.2)' }} colSpan={3}>{formData.remarks}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
            <Row className="mt-4">
              <Col className="text-center">
                <div className="d-flex flex-column flex-md-row justify-content-center gap-2">
                  <Button
                    variant="info"
                    className="mb-2 mb-md-0"
                    onClick={handleViewObservationSheet}
                    style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#ffffff' }}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    Steel Test Observation Sheet
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleViewReport}
                    style={{ backgroundColor: '#FFA500', borderColor: '#FFA500', color: '#000000' }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                    Steel Test Report
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );

  if (showPreview) {
    return <PreviewForm />;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ backgroundColor: '#6c757d', padding: '2rem 0', borderRadius: '15px', marginBottom: '2rem' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faHammer} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Steel Testing
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    Enter steel testing information and results
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button 
                as={Link} 
                to="/other-services" 
                variant="light" 
                className="btn-vitrag-primary"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Services
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        <Form onSubmit={handleSubmit}>
          {/* General Information */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    {/* Left Column */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Customer Name</Form.Label>
                        {customersLoading ? (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Loading customers...</span>
                          </div>
                        ) : customersError ? (
                          <Alert variant="warning" className="mb-0">
                            {customersError}
                          </Alert>
                        ) : (
                          <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                            <Form.Control
                              type="text"
                              name="customerName"
                              value={customerSearchTerm}
                              onChange={(e) => setCustomerSearchTerm(e.target.value)}
                              onClick={handleCustomerInputClick}
                              placeholder="Search and select customer..."
                              autoComplete="off"
                              readOnly
                              className="form-control-vitrag"
                              style={{ cursor: 'pointer' }}
                            />
                            {showCustomerDropdown && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#2d3748',
                                  border: '2px solid #4a5568',
                                  borderTop: 'none',
                                  borderRadius: '0 0 10px 10px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  zIndex: 1000,
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                {/* Search input inside dropdown */}
                                <div style={{ padding: '15px', borderBottom: '1px solid #4a5568' }}>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search customers..."
                                    value={dropdownSearchTerm}
                                    onChange={handleDropdownSearch}
                                    autoComplete="off"
                                    style={{
                                      backgroundColor: '#1a202c',
                                      border: '2px solid #4a5568',
                                      borderRadius: '8px',
                                      color: '#e2e8f0',
                                      padding: '10px 12px',
                                      fontSize: '0.9rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#FFA500';
                                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#4a5568';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </div>
                                
                                {/* Customer list */}
                                {filteredCustomers.length > 0 ? (
                                  filteredCustomers.map((customer) => (
                                    <div
                                      key={customer.id}
                                      onClick={() => handleCustomerSelect(customer)}
                                      style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #6c757d',
                                        color: 'white',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                        {customer.name}
                                      </div>
                                      <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                        {customer.phone && `${customer.phone} • `}
                                        {customer.city && customer.city}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div
                                    style={{
                                      padding: '15px',
                                      color: '#adb5bd',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {dropdownSearchTerm.trim() !== '' 
                                      ? `No customers found matching "${dropdownSearchTerm}"`
                                      : 'No customers available'
                                    }
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Quantity</Form.Label>
                        <Form.Select
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" disabled>
                            Select quantity (1–3)
                          </option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Sample Description</Form.Label>
                        <Form.Control as="textarea" rows={3} name="sample_description" value={formData.sample_description} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date of Receipt</Form.Label>
                        <Form.Control type="date" name="date_of_receipt" value={formData.date_of_receipt} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Reference Number</Form.Label>
                        <Form.Control type="text" name="reference_number" value={formData.reference_number} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Condition of Specimen</Form.Label>
                        <Form.Control type="text" name="condition_of_specimen" value={formData.condition_of_specimen} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Make</Form.Label>
                        <Form.Control type="text" name="make" value={formData.make} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Machine Used For Testing</Form.Label>
                        <Form.Control type="text" name="machine_used_for_testing" value={formData.machine_used_for_testing} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Location Test</Form.Label>
                        <Form.Control type="text" name="location_test" value={formData.location_test} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Capacity/Range</Form.Label>
                        <Form.Control type="text" name="capacity_range" value={formData.capacity_range} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Test Method</Form.Label>
                        <Form.Control type="text" name="test_method" value={formData.test_method} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Diameter of Steel</Form.Label>
                        <Form.Select
                          name="diameter_of_steel"
                          value={formData.diameter_of_steel}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" disabled>
                            Select diameter (mm)
                          </option>
                          <option value="6">6</option>
                          <option value="8">8</option>
                          <option value="10">10</option>
                          <option value="12">12</option>
                          <option value="16">16</option>
                          <option value="20">20</option>
                          <option value="25">25</option>
                          <option value="28">28</option>
                          <option value="32">32</option>
                          <option value="36">36</option>
                          <option value="40">40</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    {/* Right Column */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date Of Report</Form.Label>
                        <Form.Control type="date" name="date_of_report" value={formData.date_of_report} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3" style={{ position: 'relative' }}>
                        <Form.Label className="required-field">ULR Number</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="url_number" 
                          value={formData.url_number} 
                          onChange={(e) => {
                            handleInputChange(e);
                            const val = e.target.value;
                            const target = "Not Applicable";
                            if (val && target.toLowerCase().startsWith(val.toLowerCase()) && val.toLowerCase() !== target.toLowerCase()) {
                              setShowULRSuggestion(true);
                            } else {
                              setShowULRSuggestion(false);
                            }
                          }} 
                          placeholder="" 
                          required 
                        />
                        {showULRSuggestion && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#2d3748',
                              border: '1px solid #4a5568',
                              borderRadius: '4px',
                              zIndex: 1000,
                              cursor: 'pointer',
                              padding: '8px 12px',
                              marginTop: '2px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                            }}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                url_number: 'Not Applicable'
                              }));
                              setShowULRSuggestion(false);
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5568'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#2d3748'}
                          >
                            <span style={{ color: 'white' }}>Not Applicable</span>
                          </div>
                        )}
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Job Code Number</Form.Label>
                        <Form.Control type="text" name="job_code_number" value={formData.job_code_number} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date Of Testing</Form.Label>
                        <Form.Control type="date" name="date_of_testing" value={formData.date_of_testing} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Grade of Specimen</Form.Label>
                        <Form.Select
                          name="grade_of_specimen"
                          value={formData.grade_of_specimen}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" disabled>Select Grade</option>
                          <option value="Fe 415">Fe 415</option>
                          <option value="Fe 415D">Fe 415D</option>
                          <option value="Fe 500">Fe 500</option>
                          <option value="Fe 500D">Fe 500D</option>
                          <option value="Fe 550">Fe 550</option>
                          <option value="Fe 550D">Fe 550D</option>
                          <option value="Fe 600">Fe 600</option>
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Calibration Due Date</Form.Label>
                        <Form.Control type="date" name="calibration_due_date" value={formData.calibration_due_date} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3" style={{ position: 'relative' }}>
                        <Form.Label className="required-field">Environmental Condition</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="environmental_condition" 
                          value={formData.environmental_condition} 
                          onChange={(e) => {
                            handleInputChange(e);
                            const val = e.target.value;
                            const target = "Not Applicable";
                            if (val && target.toLowerCase().startsWith(val.toLowerCase()) && val.toLowerCase() !== target.toLowerCase()) {
                              setShowEnvSuggestion(true);
                            } else {
                              setShowEnvSuggestion(false);
                            }
                          }} 
                          placeholder="" 
                          required 
                        />
                        {showEnvSuggestion && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#2d3748',
                              border: '1px solid #4a5568',
                              borderRadius: '4px',
                              zIndex: 1000,
                              cursor: 'pointer',
                              padding: '8px 12px',
                              marginTop: '2px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                            }}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                environmental_condition: 'Not Applicable'
                              }));
                              setShowEnvSuggestion(false);
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5568'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#2d3748'}
                          >
                            <span style={{ color: 'white' }}>Not Applicable</span>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Observation of Sample & Test Result */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Observation of Sample & Test Result
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                    <table
                      className="table transparent-table"
                      style={{
                        fontSize: '1rem',
                        width: '100%',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px'
                      }}
                    >
                      <thead>
                        <tr>
                          <th
                            rowSpan={2}
                            style={{
                              width: '4%',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              padding: '12px 8px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            Sr. no
                          </th>
                          <th
                            rowSpan={2}
                            style={{
                              width: '20%',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              padding: '12px 8px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            Test Parameter
                          </th>
                          <th
                            colSpan={Math.max(1, Number(formData.quantity || 0))}
                            style={{
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              padding: '12px 8px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            Test Result
                          </th>
                          <th
                            colSpan={2}
                            style={{
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              padding: '12px 8px',
                              border: '1px solid #dee2e6'
                            }}
                            className="table-highlight-blue"
                          >
                            Specified Limits (IS 1786-2008 RA 2018)
                          </th>
                          <th
                            rowSpan={2}
                            style={{
                              width: '10%',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              padding: '12px 8px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            Method of Test / Reference
                          </th>
                        </tr>
                        <tr>
                          {(Number(formData.quantity) > 0 ? ['Bar-1', 'Bar-2', 'Bar-3'].slice(0, Number(formData.quantity)) : ['Bar-1']).map((bar, idx) => (
                            <th key={idx} style={{ width: '10%', textAlign: 'center', padding: '10px 8px', border: '1px solid #dee2e6', fontSize: '1rem' }}>
                              {bar}
                            </th>
                          ))}
                          <th
                            style={{
                              width: '10%',
                              textAlign: 'center',
                              padding: '10px 8px',
                              border: '1px solid #dee2e6',
                              fontSize: '1rem'
                            }}
                            className="table-highlight-blue"
                          >
                            Min
                          </th>
                          <th
                            style={{
                              width: '10%',
                              textAlign: 'center',
                              padding: '10px 8px',
                              border: '1px solid #dee2e6',
                              fontSize: '1rem'
                            }}
                            className="table-highlight-blue"
                          >
                            Max
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>1</td>
                          <td>Mass Per Meter (Kg/m)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.001"
                                name={`wt_per_m_${index + 1}`}
                                value={formData[`wt_per_m_${index + 1}`] || ''}
                                onChange={handleInputChange}
                                style={{ textAlign: 'center' }}
                              />
                            </td>
                          ))}
                          <td>
                            <Form.Control 
                              size="sm" 
                              type="text" 
                              style={{ textAlign: 'center' }} 
                              value={getMinMassValue(formData.diameter_of_steel)}
                              readOnly
                            />
                          </td>
                          <td>
                            <Form.Control 
                              size="sm" 
                              type="text" 
                              style={{ textAlign: 'center' }} 
                              value={getMaxMassValue(formData.diameter_of_steel)}
                              readOnly
                            />
                          </td>
                          <td>IS 1786: 2008 (RA 2018)</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>2</td>
                          <td>Cross Sectional Area (mm²)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`c_area_${index + 1}`}
                                value={formData[`c_area_${index + 1}`] || ''}
                                readOnly
                                style={{ textAlign: 'center', backgroundColor: '#f8f9fa' }}
                              />
                            </td>
                          ))}
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>-----</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>3</td>
                          <td>% of Elongation</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`elongation_${index + 1}`}
                                value={formData[`elongation_${index + 1}`] || ''}
                                onChange={handleInputChange}
                                style={{ textAlign: 'center' }}
                              />
                            </td>
                          ))}
                          <td>
                            <Form.Control 
                              size="sm" 
                              type="text" 
                              style={{ textAlign: 'center' }} 
                              value={getMinElongationValue(formData.grade_of_specimen)}
                              readOnly
                            />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>IS 1608 Part-1: 2022</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>4</td>
                          <td>Yield Stress (N/mm²)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="1"
                                name={`proof_yield_stress_${index + 1}`}
                                value={formData[`proof_yield_stress_${index + 1}`] || ''}
                                onChange={handleInputChange}
                                style={{ textAlign: 'center' }}
                              />
                            </td>
                          ))}
                          <td>
                            <Form.Control 
                              size="sm" 
                              type="text" 
                              style={{ textAlign: 'center' }} 
                              value={getMinYieldStressValue(formData.grade_of_specimen)}
                              readOnly
                            />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>IS 1608 Part-1: 2022</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>5</td>
                          <td>Tensile Strength (N/mm²)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="1"
                                name={`ultimate_tensile_stress_${index + 1}`}
                                value={formData[`ultimate_tensile_stress_${index + 1}`] || ''}
                                onChange={handleInputChange}
                                style={{ textAlign: 'center' }}
                              />
                            </td>
                          ))}
                          <td>
                            <Form.Control 
                              size="sm" 
                              type="text" 
                              style={{ textAlign: 'center' }} 
                              value={getMinTensileStrengthValue(formData.grade_of_specimen)}
                              readOnly
                            />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>IS 1599: 2023</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>6</td>
                          <td>Bend Test (180°)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>{bendRebendSelect(index + 1)}</td>
                          ))}
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>-----</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'center', fontWeight: 'bold' }}>7</td>
                          <td>Re-Bend Test (123° / 157.5°)</td>
                          {observationBarKeys.map((bar, index) => (
                            <td key={bar}>{bendRebendSelect(index + 1)}</td>
                          ))}
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>
                            <Form.Control size="sm" type="text" style={{ textAlign: 'center' }} defaultValue="----" />
                          </td>
                          <td>IS 1786: 2008 (RA 2018)</td>
                        </tr>
                      </tbody>
                    </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Test Status */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Status
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <div className="table-responsive">
                    {renderTestStatusTable()}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Verification */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Verification
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={4}>
                      <h6>Tested By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Name</Form.Label>
                        <Form.Control type="text" name="tested_by_name" value={formData.tested_by_name} onChange={handleInputChange} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date</Form.Label>
                        <Form.Control type="date" name="tested_by_date" value={formData.tested_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Checked By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Name</Form.Label>
                        <Form.Control type="text" name="checked_by_name" value={formData.checked_by_name} onChange={handleInputChange} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date</Form.Label>
                        <Form.Control type="date" name="checked_by_date" value={formData.checked_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Verified By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="verified_by_name" value={formData.verified_by_name} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="required-field">Date</Form.Label>
                        <Form.Control type="date" name="verified_by_date" value={formData.verified_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Remarks */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faComment} className="me-2" />
                    Remarks
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Control as="textarea" rows={4} name="remarks" value={formData.remarks} onChange={handleInputChange} placeholder="" />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Review & Authorization */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Reviewed By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Select Reviewer:</Form.Label>
                    {loadingReviewers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading reviewers...</span>
                      </div>
                    ) : (
                      <Form.Select 
                        id="reviewer_select" 
                        onChange={handleReviewerChange}
                        value={reviewerInfo.name}
                        className="form-select"
                      >
                        {reviewers.map((reviewer) => (
                          <option 
                            key={reviewer.id} 
                            value={reviewer.name} 
                            data-name={reviewer.name} 
                            data-designation={reviewer.designation} 
                            data-graduation={reviewer.graduation}
                          >
                            {reviewer.name} - {reviewer.designation}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Authorized By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Authorized By:</Form.Label>
                    <Form.Control type="text" name="authorized_by" value={formData.authorized_by} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <Button type="submit" variant="primary" size="lg" className="me-3" disabled={isSaving}>
                {isSaving ? <><Spinner animation="border" size="sm" className="me-2" /> Saving...</> : <><FontAwesomeIcon icon={faSave} className="me-2" /> Save Test Data</>}
              </Button>
              <Button as={Link} to="/other-services" variant="secondary" size="lg" className="px-5">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default SteelTesting;

